import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, errMsg, formatCurrency } from "../lib/api";
import { Users, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResourceLoadPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/resources/load"); setRows(data); }
    catch(e){ toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(()=>{ load(); }, []);

  const overbooked = rows.filter(r => r.is_over_booked).length;

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>Resource load</h1>
          <p className="text-slate-400 mt-1">Per-teammate planned workload across all your projects.</p>
        </div>
        <div className="pc-card px-5 py-3 flex items-center gap-3" data-testid="resource-summary">
          <Users className="text-blue-400"/>
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-300/80">Overbooked</div>
            <div className="text-xl font-semibold text-white" style={{fontFamily:'Outfit'}}>{overbooked} of {rows.length}</div>
          </div>
        </div>
      </div>

      {loading ? <div className="text-slate-400">Loading…</div>
        : rows.length===0 ? <div className="pc-card p-10 text-center text-slate-400">No teammates yet. Add some to see workload.</div>
        : <div className="pc-card overflow-x-auto" data-testid="resource-table">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
                <tr>
                  <th className="p-3">Teammate</th>
                  <th className="p-3">Role</th>
                  <th className="p-3 w-40">Planned hours</th>
                  <th className="p-3 w-64">Utilization</th>
                  <th className="p-3">Open tasks</th>
                  <th className="p-3">Est cost</th>
                  <th className="p-3">Projects</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {rows.map(r=> {
                  const pct = Math.min(200, r.utilization_pct || 0);
                  const barClass = r.is_over_booked ? "from-rose-500 to-rose-400" : pct>75 ? "from-amber-500 to-amber-400" : "from-blue-500 to-sky-400";
                  return (
                    <tr key={r.teammate_id} data-testid={`resource-row-${r.teammate_id}`} className="hover:bg-slate-900/40">
                      <td className="p-3 text-white font-medium">{r.name}</td>
                      <td className="p-3 text-slate-300">{r.role_name || "—"}</td>
                      <td className="p-3 mono text-slate-200">{r.total_est_hours.toFixed(1)} h</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${barClass}`} style={{width: `${Math.min(100, pct)}%`}}></div>
                          </div>
                          <span className="mono text-xs text-slate-300 w-14 text-right">{pct.toFixed(0)}%</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">of {r.threshold_hours}h capacity</div>
                      </td>
                      <td className="p-3 text-slate-200">{r.open_tasks}</td>
                      <td className="p-3 mono text-slate-200">{formatCurrency(r.total_est_cost)}</td>
                      <td className="p-3 text-slate-300">{(r.projects||[]).join(", ") || "—"}</td>
                      <td className="p-3">
                        {r.is_over_booked
                          ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"><AlertTriangle size={12}/> Overbooked</span>
                          : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><CheckCircle2 size={12}/> OK</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>}
    </AppLayout>
  );
}
